import { useQuery } from '@tanstack/react-query';
import { useEntity } from '@/hooks/useEntity';
import { getBillingOverview } from '../api/billing.api';
import { billingKeys } from './billing.keys';

/**
 * The single client-side source of plan, status and seat figures (§14).
 *
 * No component reads subscription state any other way, and none derives "is
 * this startup premium" from a plan name or a price: `canCancel`,
 * `canManageAutoRenew` and the seat figures all arrive decided. Two
 * implementations of one entitlement rule diverge, and the backend's is the
 * one DAXE agrees with.
 *
 * Disabled when there is no acting entity - an individual or a Super Admin has
 * no startup whose billing this could be.
 *
 * @param options.enabled Additional gate for callers outside the billing
 *   screens. The whole billing controller requires `BILLING_MANAGE`, so the
 *   team page (MYN-49.3) must not ask on behalf of a member who would only be
 *   answered with a 403.
 */
export function useSubscription({ enabled = true }: { enabled?: boolean } = {}) {
  const entity = useEntity();
  const entityId = entity?.entityId;

  return useQuery({
    queryKey: billingKeys.overview(entityId ?? 'none'),
    queryFn: () => getBillingOverview(entityId as string),
    enabled: enabled && Boolean(entityId),
  });
}
